import { useEffect, useState } from "react";
import { adminApi } from "../services/api";
import { useAuthStore } from "../stores/authStore";

type CostGroup = { Keys: string[]; Metrics: { UnblendedCost: { Amount: string; Unit: string } } };
type ResultByTime = { TimePeriod: { Start: string; End: string }; Groups: CostGroup[] };

function AwsCostBreakdown({ resultsByTime }: { resultsByTime: ResultByTime[] }) {
  const byService: Record<string, number> = {};
  let grandTotal = 0;
  for (const day of resultsByTime) {
    for (const g of day.Groups ?? []) {
      const svc = g.Keys[0] ?? "Other";
      const amt = parseFloat(g.Metrics.UnblendedCost.Amount ?? "0");
      byService[svc] = (byService[svc] ?? 0) + amt;
      grandTotal += amt;
    }
  }
  const sorted = Object.entries(byService).sort((a, b) => b[1] - a[1]);
  const fmt = (n: number) => `$${n.toFixed(4)}`;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {resultsByTime[0]?.TimePeriod.Start} — {resultsByTime[resultsByTime.length - 1]?.TimePeriod.End}
        </span>
        <span style={{ fontWeight: 800, color: "#fca5a5" }}>Total: {fmt(grandTotal)}</span>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <tbody>
          {sorted.map(([svc, amt]) => (
            <tr key={svc} style={{ borderTop: "1px solid var(--surface2)" }}>
              <td style={{ padding: "5px 0", color: "var(--text)" }}>{svc}</td>
              <td style={{ padding: "5px 0", textAlign: "right", color: amt > 0 ? "#fcd34d" : "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}>{fmt(amt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const DAY_OPTIONS = [7, 14, 30, 90] as const;

export default function Metrics() {
  const { hasPermission, role } = useAuthStore();
  const [metrics, setMetrics] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [costDays, setCostDays] = useState<number>(7);
  const [costLoading, setCostLoading] = useState(false);

  useEffect(() => {
    adminApi.getMetrics(costDays).then(setMetrics).catch(() => {}).finally(() => setLoading(false));
  }, []);

  function changeCostDays(days: number) {
    setCostDays(days);
    setCostLoading(true);
    adminApi.getMetrics(days)
      .then((data) => setMetrics((prev) => prev ? { ...prev, awsCost: data.awsCost, awsCostDays: data.awsCostDays } : data))
      .catch(() => {})
      .finally(() => setCostLoading(false));
  }

  if (!hasPermission("metrics:read")) {
    return <div className="card" style={{ color: "var(--text-muted)" }}>metrics:read permission required.</div>;
  }
  if (loading) return <div style={{ color: "var(--text-muted)" }}>Loading metrics…</div>;
  if (!metrics) return <div style={{ color: "#f87171" }}>Failed to load metrics.</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <h1 style={{ fontSize: 20, fontWeight: 900 }}>📈 Metrics</h1>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
        {[
          { label: "Total Families",  value: metrics.totalFamilies,    color: "#a5b4fc" },
          { label: "Active (7d)",     value: metrics.activeSevenDays,   color: "#86efac" },
          { label: "Active (30d)",    value: metrics.activeThirtyDays,  color: "#86efac" },
          { label: "Total Kids",      value: metrics.totalKids,          color: "#fcd34d" },
          { label: "Total Quests",    value: metrics.totalCompletions,   color: "#fcd34d" },
          { label: "Stars Earned",    value: metrics.totalStarsEarned,   color: "#f9a8d4" },
        ].map((s) => (
          <div key={s.label} className="card">
            <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: s.color }}>{s.value?.toLocaleString() ?? "—"}</div>
          </div>
        ))}
      </div>

      {metrics.themeDistribution && (
        <div className="card">
          <div style={{ fontWeight: 800, marginBottom: 12 }}>Theme Distribution</div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {Object.entries(metrics.themeDistribution as Record<string, number>).map(([theme, count]) => (
              <div key={theme} style={{ display: "flex", flex: 1, minWidth: 120, flexDirection: "column", gap: 4 }}>
                <div style={{ fontWeight: 600, fontSize: 13, textTransform: "capitalize" }}>{theme}</div>
                <div style={{ background: "var(--surface2)", borderRadius: 4, overflow: "hidden", height: 8 }}>
                  <div style={{ height: "100%", background: "var(--accent)", width: `${Math.min(100, (count / (metrics.totalFamilies || 1)) * 100)}%` }} />
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{count} families</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {role === "root" && (
        <div className="card" style={{ border: "1px solid rgba(239,68,68,0.2)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ fontWeight: 800, color: "#fca5a5", display: "flex", alignItems: "center", gap: 8 }}>
              🔑 AWS Cost (StarQuest only)
              <span className="badge badge-root">Root only</span>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {DAY_OPTIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => changeCostDays(d)}
                  style={{
                    padding: "3px 10px",
                    fontSize: 11,
                    borderRadius: 4,
                    border: "1px solid var(--surface2)",
                    background: costDays === d ? "var(--accent)" : "var(--surface2)",
                    color: costDays === d ? "#fff" : "var(--text-muted)",
                    cursor: "pointer",
                    fontWeight: costDays === d ? 700 : 400,
                  }}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
          {costLoading ? (
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Loading…</div>
          ) : !metrics.awsCost ? (
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Cost data unavailable — Cost Explorer may not be enabled, or no resources are tagged <code>Project=starquest</code> yet.</div>
          ) : (
            <AwsCostBreakdown resultsByTime={metrics.awsCost} />
          )}
        </div>
      )}
    </div>
  );
}
